/* eslint-disable react/jsx-no-comment-textnodes */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { up } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { useMediaQuery } from 'usehooks-ts';

import { ReactComponent as BackgroundHoverSvg } from '../../assets/images/Subtract_white_md.svg';
import { ReactComponent as BackgroundHoverSmallSvg } from '../../assets/images/Subtract_white_sm.svg';
import { ReactComponent as BackgroundSvg } from '../../assets/images/Subtract_yellow_md.svg';
import { ReactComponent as BackgroundSmallSvg } from '../../assets/images/Subtract_yellow_sm.svg';
import { MaoriLanguage } from '../Accessibility/MaoriLanguage';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionLabel,
  AccordionPanel,
} from '../Accordion/Accordion';
import { recordCustomEvents } from '../Analytics/RecordEvents';
import { BackgroundBlockRoot } from '../AnimatedBackground/AnimatedBackground';
import { Block } from '../Block/Block';
import { Box } from '../Box/Box';
import { LinkButton } from '../Button/Button';
import { ScrollToTopButton } from '../Button/ScrollToTopButton';
import { ButtonLink } from '../ButtonLink/ButtonLink';
import { Container4 } from '../Container/Container2';
import { FooterNavigation } from '../FooterNavigation/FooterNavigation';
import { GlobalHeader } from '../GlobalHeader/GlobalHeader';
import { Head } from '../Head/Head';
import { HorizontalSeparator } from '../HorizontalSeperator/HorizontalSeparator';
import { LandingSections } from '../LandingContextProvider/LandingContextProvider';
import { useLandingContext } from '../LandingContextProvider/useLandingContext';
import { Body2, Body3, Body4, Body5, H1, Ul } from '../Typography/Typography';

import { useAuth } from '@/features/auth';
import { Navigation } from '@/features/iwiAffiliationForm/components/Navigation';

const BACKGROUND_IMAGE_MAPPINGS: {
  [key: string]: { small: string; large: string };
} = {
  pango: {
    small: '/backgrounds/background-pango-small.png',
    large: '/backgrounds/background-pango.jpg',
  },
};

export const Questions = () => {
  const [isHover, setIsHover] = useState<boolean>(false);
  const [isHoverDiv2, setIsHoverDiv2] = useState<boolean>(false);

  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  /* We refocus the main title when using the back to top button */
  const mainTitleRef = useRef<HTMLHeadingElement>(null);
  const {
    sectionConfig: { textColor, blockBackgroundVariant },
    currentSection,
    setCurrentSection,
  } = useLandingContext();

  const handleInViewChange = (inView: boolean, sectionId: LandingSections) => {
    if (inView && sectionId !== currentSection) {
      setCurrentSection(sectionId);
    }
  };
  const isDesktop = useBreakpoint(up('md'));
  const currentSize = isDesktop ? 'large' : 'small';
  const isDesktopLg = useMediaQuery('(min-width: 1457px)');
  // const isDesktopLg = useBreakpoint(up('lg'));
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <Head />

      <GlobalHeader />

      {/* <PrimaryNavigation /> */}

      <main>
        {!isAuthenticated && (
          <Box
            position={{ _: 'relative', md: 'fixed' }}
            display="flex"
            justifyContent="space-between"
            top={{ md: 32 }}
            right={{ _: 0, md: 32 }}
            bottom={{ md: 'unset' }}
            left={{ _: 0, md: 'unset' }}
            px={{ _: 24, md: 0 }}
            py={{ _: 12, md: 0 }}
            width={{ _: '100%', md: 'auto' }}
            flexShrink={0}
            flexGrow={0}
            zIndex="11"
            flexDirection="column"
          >
            {isDesktop && (
              <>
                <LinkButton
                  label="Get started"
                  to="/signin"
                  dataCy="get-started-button"
                  variant={blockBackgroundVariant === 'dark' && isDesktop ? 'light' : 'dark'}
                />
              </>
            )}
            <Box textAlign="end" mt="24px">
              <ButtonLink
                color={textColor}
                onClick={() => {
                  recordCustomEvents('login_with_mha', { login_with_mha: true });
                  login();
                }}
              >
                Sign in
              </ButtonLink>
            </Box>
          </Box>
        )}
        {/* <AnimatedBackground /> */}
        <BackgroundBlockRoot
          data-cy="background-block-root"
          key="harakeke"
          backgroundColor="harakeke"
          backgroundImageUrl={BACKGROUND_IMAGE_MAPPINGS['pango'][currentSize]}
          active={true}
        />
        <Block
          inViewThreshold={currentSection === LandingSections.pataiContinued ? 1 : undefined}
          variant="light"
          onInViewChange={(inView) => {
            if (inView) {
              handleInViewChange(inView, LandingSections.pataiContinued);
            }
          }}
        >
          <Container4>
            <Navigation
              title="Back"
              goToPreviousStep={() => {
                window.scrollTo(0, 0);
                navigate(-1);
              }}
              labelled-by="questions"
              padding="0px"
              marginTop={{ _: '72px' }}
            >
              <Box marginTop="40px" marginBottom={{ _: '32px', xxl: '72px' }} width="100%" maxWidth={{ md: 920 }}>
                <H1 as="h2" color={textColor} textAlign={{ _: 'left', md: 'center' }}>
                  <MaoriLanguage>Pātai</MaoriLanguage>
                </H1>
                <HorizontalSeparator color={textColor} justifyContent={{ _: 'left', md: 'center' }} />

                <Accordion collapsible>
                  <AccordionItem>
                    <AccordionButton>
                      <AccordionLabel>Who is collecting my data?</AccordionLabel>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel>
                      <Body2 mb="1rem">
                        The initial collection, sharing, access to, and use of <MaoriLanguage>iwi</MaoriLanguage>{' '}
                        affiliation data for <MaoriLanguage>iwi</MaoriLanguage> will be managed by Ministry of Health |{' '}
                        <MaoriLanguage>Manatū Hauora</MaoriLanguage>.
                      </Body2>
                      <Body2 mb="1rem">
                        A multi-party governance group, including <MaoriLanguage>iwi</MaoriLanguage> representatives, is
                        mandated to uphold <MaoriLanguage>Māori</MaoriLanguage> perspectives, ensure purpose-limitation
                        consistent with the collection notice, and guide system development toward iwi-led data
                        decision-making.
                      </Body2>
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <AccordionLabel>Why are you collecting it?</AccordionLabel>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel>
                      <Body2 mb="1rem">
                        This data is being collected to provide iwi and Ministry of Health |{' '}
                        <MaoriLanguage>Manatū Hauora</MaoriLanguage> with information to help improve the health and
                        wellbeing of <MaoriLanguage>Māori</MaoriLanguage>.
                      </Body2>
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <AccordionLabel>How will my data be used?</AccordionLabel>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel>
                      <Body2 mb="1rem">
                        We respect your right to control your information. If you choose to share your{' '}
                        <MaoriLanguage>iwi</MaoriLanguage> affiliation, we will use it to:
                      </Body2>
                      <Body2 as="div" mb="1rem">
                        <Ul>
                          <li>
                            Improve <MaoriLanguage>Māori</MaoriLanguage> health and wellbeing through better,
                            evidence-based decisions.
                          </li>
                          <li>
                            Support <MaoriLanguage>iwi</MaoriLanguage> to design programmes, services, interventions and
                            iwi-led policies.
                          </li>
                          <li>
                            Inform improved allocation of resources and funding to <MaoriLanguage>iwi</MaoriLanguage>.
                          </li>
                          <li>
                            Support <MaoriLanguage>iwi</MaoriLanguage> planning and social and economic development.
                          </li>
                          <li>
                            Help central, regional and local government agencies, <MaoriLanguage>iwi</MaoriLanguage>{' '}
                            organisations, NGOs and researchers (where appropriate) to evaluate, monitor, plan and
                            deliver services with and for <MaoriLanguage>iwi</MaoriLanguage>.
                          </li>
                        </Ul>
                        <Body3 as="p" fontStyle="italic">
                          Purpose-limitation, governance oversight (including <MaoriLanguage>iwi</MaoriLanguage>{' '}
                          representation), and de-identification/aggregation will apply as appropriate. You retain
                          rights of access and correction under the Privacy Act 2020.
                        </Body3>
                      </Body2>
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <AccordionLabel>
                        Will you share this data with my <MaoriLanguage>iwi</MaoriLanguage>?
                      </AccordionLabel>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel>
                      <Body2 mb="1rem">
                        Information about <MaoriLanguage>iwi</MaoriLanguage> affiliation and other relevant health data
                        may be shared with <MaoriLanguage>iwi</MaoriLanguage> in an aggregated format only, no
                        personally identifiable information will be included.
                      </Body2>
                      <Body2 mb="1rem">
                        This ensures your privacy is protected, while supporting <MaoriLanguage>iwi</MaoriLanguage> to
                        better understand the wider connections of their <MaoriLanguage>whānau</MaoriLanguage> across{' '}
                        <MaoriLanguage>Aotearoa</MaoriLanguage>.
                      </Body2>
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <AccordionLabel>Who owns this information, and can I update it?</AccordionLabel>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel>
                      <Body2 mb="1rem">
                        You do! This is your information, and you are in control of it. Once you are logged in, you can
                        update, edit, or delete your information anytime you want.
                      </Body2>
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <AccordionLabel>How will my information be stored?</AccordionLabel>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel>
                      <Body2 mb="1rem">
                        The information will be stored in a secure, Australia-hosted cloud environment. Access is
                        limited to authorised project personnel and used only for analysis at an aggregated level.
                        Design of the next phase will be guided by <MaoriLanguage>Māori</MaoriLanguage> data experts and
                        the governance group to determine ongoing storage, governance, and access settings. This also
                        includes the transition of stewardship to <MaoriLanguage>iwi</MaoriLanguage>{' '}
                        <MaoriLanguage>Māori</MaoriLanguage>.
                      </Body2>
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <AccordionLabel>
                        What if I am of <MaoriLanguage>Māori</MaoriLanguage> descent but I do not know the name of my{' '}
                        <MaoriLanguage>iwi</MaoriLanguage>?
                      </AccordionLabel>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel>
                      <Body2 mb="1rem">
                        If you are unsure of the name of your <MaoriLanguage>iwi</MaoriLanguage>, we have provided a map
                        and a list that you can filter by region to view all <MaoriLanguage>iwi</MaoriLanguage> from
                        that <MaoriLanguage>rohe</MaoriLanguage>.
                      </Body2>
                      <Body2 mb="1rem">
                        If you are still unsure after reviewing the list, we encourage you to talk with your{' '}
                        <MaoriLanguage>whānau</MaoriLanguage> and return to the website to provide this information at a
                        later time. You can still record that you are of <MaoriLanguage>Māori</MaoriLanguage> descent
                        without identifying your <MaoriLanguage>iwi</MaoriLanguage>.
                      </Body2>
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <AccordionLabel>Who is the Data Iwi Leaders Group?</AccordionLabel>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel>
                      <Body2 mb="1rem">
                        Data Iwi Leaders Group is a group of iwi governance representatives who advocate for{' '}
                        <MaoriLanguage>iwi</MaoriLanguage> access and control over data.
                      </Body2>
                      <Body2 mb="1rem">
                        The group was established by National Iwi Chairs Forum in 2016 to harness the potential of data
                        for <MaoriLanguage>iwi</MaoriLanguage> development and to advance the data{' '}
                        <MaoriLanguage>kaupapa</MaoriLanguage> with the Crown. The group consists of six representatives
                        from different <MaoriLanguage>iwi</MaoriLanguage>. Please visit their website here{' '}
                        <a
                          href="https://www.kahuiraraunga.io/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontWeight: 'bold', textDecoration: 'underline' }}
                        >
                          https://www.kahuiraraunga.io/
                        </a>{' '}
                        to find out more.
                      </Body2>
                    </AccordionPanel>
                  </AccordionItem>
                  <AccordionItem>
                    <AccordionButton>
                      <AccordionLabel>Who are the multi-party governance groups?</AccordionLabel>
                      <AccordionIcon />
                    </AccordionButton>

                    <AccordionPanel>
                      <Body2 mb="1rem">
                        <MaoriLanguage>Māori</MaoriLanguage> data experts from the Data Iwi Leaders Group have partnered
                        with the Ministry of Health | <MaoriLanguage>Manatū Hauora</MaoriLanguage> and Health New
                        Zealand | <MaoriLanguage>Te Whatu Ora</MaoriLanguage> on this kaupapa. Included in this
                        collaboration are <MaoriLanguage>Māori</MaoriLanguage> data experts from{' '}
                        <MaoriLanguage>Te Puni Kōkiri</MaoriLanguage>, Stats NZ and the Ministry of Education.
                      </Body2>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </Box>
              <StyledOuterDiv>
                <Box
                  style={{
                    position: 'relative',
                  }}
                  onClick={() => navigate('/about', { state: { previousStep: 'questions' } })}
                  onMouseEnter={() => {
                    return setIsHover(true);
                  }}
                  onMouseLeave={() => {
                    return setIsHover(false);
                  }}
                >
                  {isDesktopLg ? (
                    <>
                      {!isHover ? (
                        <div>
                          <StyledBackgroundSvg />
                        </div>
                      ) : (
                        <div
                          style={{
                            position: 'relative',
                          }}
                        >
                          <StyledBackgroundHoverSvg />
                        </div>
                      )}
                      <StyledImageDiv>
                        <Box>
                          <StyledImg1 src="/backgrounds/whakapapa_about.png" alt="" />
                        </Box>
                        <StyledContentDiv>
                          <Body5 color="#333333">
                            <MaoriLanguage>Whakapapa</MaoriLanguage>{' '}
                          </Body5>{' '}
                          <Body4 color="#333333" marginLeft="6px">
                            {' '}
                            (About)
                          </Body4>
                        </StyledContentDiv>
                      </StyledImageDiv>
                    </>
                  ) : (
                    <>
                      {' '}
                      {!isHover ? (
                        <div
                        // style={{
                        //   position: 'relative',
                        // }}
                        >
                          <StyledBackgroundSmallSvg />
                        </div>
                      ) : (
                        <div
                          style={{
                            position: 'relative',
                          }}
                        >
                          <StyledBackgroundHoverSmallSvg />
                        </div>
                      )}
                      <StyledImageDiv>
                        <Box>
                          <StyledImg1 src="/backgrounds/whakapapa_about.png" alt="" />
                        </Box>
                        <StyledContentDiv>
                          <Body5 color="#333333">
                            <MaoriLanguage>Whakapapa</MaoriLanguage>{' '}
                          </Body5>{' '}
                          <Body4 color="#333333" marginLeft="6px">
                            {' '}
                            (About)
                          </Body4>
                        </StyledContentDiv>
                      </StyledImageDiv>
                    </>
                  )}
                </Box>
                <Box
                  style={{
                    position: 'relative',
                  }}
                  onClick={() => navigate('/benefits', { state: { previousStep: 'questions' } })}
                  onMouseEnter={() => {
                    return setIsHoverDiv2(true);
                  }}
                  onMouseLeave={() => {
                    return setIsHoverDiv2(false);
                  }}
                >
                  {isDesktopLg ? (
                    <>
                      {!isHoverDiv2 ? (
                        <div
                          style={{
                            position: 'relative',
                          }}
                        >
                          <StyledBackgroundSvg />
                        </div>
                      ) : (
                        <div
                          style={{
                            position: 'relative',
                          }}
                        >
                          <StyledBackgroundHoverSvg />
                        </div>
                      )}

                      <StyledImageDiv>
                        <Box>
                          <StyledImg src="/backgrounds/whainga_benefits.png" alt="" />
                        </Box>
                        <StyledContentDiv>
                          <Body5 color="#333333">
                            <MaoriLanguage>Whāinga</MaoriLanguage>{' '}
                          </Body5>{' '}
                          <Body4 color="#333333" marginLeft="6px">
                            {' '}
                            (Benefits)
                          </Body4>
                        </StyledContentDiv>
                      </StyledImageDiv>
                    </>
                  ) : (
                    <>
                      {!isHoverDiv2 ? (
                        <div
                          style={{
                            position: 'relative',
                          }}
                        >
                          <StyledBackgroundSmallSvg />
                        </div>
                      ) : (
                        <div
                          style={{
                            position: 'relative',
                          }}
                        >
                          <StyledBackgroundHoverSmallSvg />
                        </div>
                      )}

                      <StyledImageDiv>
                        <Box>
                          <StyledImg src="/backgrounds/whainga_benefits.png" alt="" />
                        </Box>
                        <StyledContentDiv>
                          <Body5 color="#333333">
                            <MaoriLanguage>Whāinga</MaoriLanguage>{' '}
                          </Body5>{' '}
                          <Body4 color="#333333" marginLeft="6px">
                            {' '}
                            (Benefits)
                          </Body4>
                        </StyledContentDiv>
                      </StyledImageDiv>
                    </>
                  )}
                </Box>
              </StyledOuterDiv>
            </Navigation>
          </Container4>
        </Block>
        <Box position={{ _: 'static' }} bottom={0} width={{ md: '100%' }} zIndex={10} mt="auto">
          <FooterNavigation />

          {!isDesktop && (
            <Box mt={32} display="flex" justifyContent={{ _: 'center' }} flexShrink={0} flexGrow={0}>
              <ScrollToTopButton focusRef={mainTitleRef} />
            </Box>
          )}
        </Box>
      </main>
    </>
  );
};

const StyledBackgroundSvg = styled(BackgroundSvg)`
  height: 377px;

  ${up('md')} {
    height: 377px;
  }

  ${up('lg')} {
    height: 314px;
  }
  ${up('xl')} {
    height: 314px;
  }
  ${up('xxl')} {
    height: 314px;
  }
`;

const StyledBackgroundHoverSvg = styled(BackgroundHoverSvg)`
  height: 377px;

  ${up('md')} {
    height: 377px;
  }

  ${up('lg')} {
    height: 314px;
  }
  ${up('xl')} {
    height: 314px;
  }
  ${up('xxl')} {
    height: 314px;
  }
`;
const StyledBackgroundSmallSvg = styled(BackgroundSmallSvg)`
  height: 377px;

  ${up('md')} {
    height: 314px;
  }

  ${up('lg')} {
    height: 314px;
  }
  ${up('xl')} {
    height: 314px;
  }
  ${up('xxl')} {
    height: 314px;
  }
`;

const StyledBackgroundHoverSmallSvg = styled(BackgroundHoverSmallSvg)`
  height: 377px;

  ${up('md')} {
    height: 377px;
  }

  ${up('lg')} {
    height: 314px;
  }
  ${up('xl')} {
    height: 314px;
  }
  ${up('xxl')} {
    height: 314px;
  }
`;

const StyledImageDiv = styled.div`
  width: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  padding: 16px;
`;

const StyledOuterDiv = styled.div`
  // width: 100%;
  margin-bottom: 72px;
  margin-top: 32px;
  display: grid;
  grid-template-columns: 100%;

  gap: 16px;

  @media only screen and (min-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1em 16px;
  }

  ${up('xl')} {
    grid-template-columns: repeat(2, 1fr);
    gap: 1em 32px;
  }
  ${up('xxl')} {
    grid-template-columns: repeat(2, 1fr);
    gap: 1em 32px;
  }
`;

const StyledContentDiv = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: baseline;
  color: white;
  align-content: end;
  height: 62px;
  padding: 15px;
  @media only screen and (min-width: 991px) {
    padding: 15px;
  }

  ${up('xl')} {
    padding: 10px;
  }

  @media only screen and (min-width: 1600px) {
    padding: 10px;
  }
`;

const StyledImg1 = styled.img`
  width: 100%;
  height: 100%;
  object-position: 50% 10%;
  object-fit: cover;
  max-height: 295px;
  color: #333333;
  :hover {
    color: #666666;
  }
  ${up('md')} {
    max-height: 232px;
  }
  ${up('lg')} {
    max-height: 232px;
  }
  ${up('xl')} {
    max-height: 232px;
  }
  ${up('xxl')} {
    max-height: 232px;
  }
`;
const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-position: top center;
  object-fit: cover;
  max-height: 295px;
  color: #333333;
  :hover {
    color: #666666;
  }
  ${up('md')} {
    max-height: 232px;
  }
  ${up('lg')} {
    max-height: 232px;
  }
  ${up('xl')} {
    max-height: 232px;
  }
  ${up('xxl')} {
    max-height: 232px;
  }
`;
